import * as React from 'react';
import { Button, Caption1, Input, Label, Link, makeStyles, tokens } from '@fluentui/react-components';
import { DeleteRegular } from '@fluentui/react-icons';
import { FormFieldDescription } from '../FormFieldDescription';

export const urlRegex = new RegExp(
  /^(https:\/\/.)[-a-zA-Z0-9@:%._+~#=]{1,}\.[a-zA-Z]{2,30}\b([-a-zA-Z0-9@:%_+.~#?&/=!]*)$/
);

interface Props
  extends Partial<React.ComponentProps<typeof Input>>,
    Omit<React.ComponentProps<typeof FormFieldDescription>, 'id'> {
  label?: React.ReactNode;
  required?: boolean;
  limit?: number;
  onValuesChange: (values: Array<string>) => void;
  removeLabel?: string;
  values: Array<string>;
  addAnotherLabel?: string;
  urlValidationErrorMessage?: string;
}

export function MultiTextField({
  label,
  hint,
  validationMessage,
  validationState,
  limit = Infinity,
  onValuesChange,
  removeLabel = 'Remove',
  values,
  addAnotherLabel = '+ Add another item',
  ...props
}: Props) {
  const id = React.useId();
  const [invalidInput, setInvalidInput] = React.useState<Array<number>>([]);
  const fieldId = props.id || id;
  const classes = useStyles();

  return (
    <div className={classes.root}>
      {label ? (
        <Label id={fieldId} required={props.required}>
          {label}
        </Label>
      ) : null}

      <div className={classes.inputs}>
        {(values.length === 0 ? values.concat('') : values).map((value, i) => (
          <div className={classes.row} key={i}>
            <div className={classes.column}>
              <Input
                {...props}
                className={classes.input}
                aria-labelledby={props['aria-labelledby'] || fieldId}
                aria-invalid={validationState === 'error'}
                aria-errormessage={validationState === 'error' ? `error-${id}` : undefined}
                aria-describedby={hint ? `desc-${id}` : undefined}
                value={value}
                onBlur={(e) => {
                  if (!value && i == 0) {
                    onValuesChange([]);
                  }
                  props.onBlur && props.onBlur(e);
                }}
                onChange={(_, data) => {
                  const newValues = [...values];
                  newValues[i] = data.value;

                  // validate input if it is a url
                  if (props?.type && props?.type === 'url') {
                    const urlIsValid = urlRegex.test(data.value);
                    if (!urlIsValid) {
                      if (!invalidInput.includes(i)) {
                        setInvalidInput([...invalidInput, i]);
                      }
                    } else if (invalidInput.includes(i)) {
                      const copyOfInvalidInput = [...invalidInput];
                      copyOfInvalidInput.splice(invalidInput.indexOf(i), 1);
                      setInvalidInput(copyOfInvalidInput);
                    }
                  }
                  onValuesChange(newValues);
                }}
              />
              {invalidInput.length > 0 && invalidInput.includes(i) && props.urlValidationErrorMessage && (
                <Caption1 className={classes.validationErrorMessage}>{props.urlValidationErrorMessage}</Caption1>
              )}
            </div>
            {i !== 0 && !props.disabled && (
              <Button
                aria-label={removeLabel}
                onClick={() => {
                  let newValues = [...values];
                  newValues.splice(i, 1);
                  if (newValues.length === 1 && newValues[0] === '') {
                    newValues = [];
                  }
                  onValuesChange(newValues);
                }}
                appearance="subtle"
                icon={<DeleteRegular />}
              />
            )}
          </div>
        ))}
      </div>

      <FormFieldDescription
        id={fieldId}
        hint={hint}
        validationMessage={validationMessage}
        validationState={validationState}
      />

      {values.length < limit && !props.disabled && (
        <Link
          onClick={() => {
            const newValues = [...(values.length === 0 ? values.concat('') : values)];
            newValues.push('');
            onValuesChange(newValues);
          }}
        >
          {addAnotherLabel}
        </Link>
      )}
    </div>
  );
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: tokens.spacingVerticalL,
  },
  inputs: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
  },
  input: {
    width: '100%',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  validationErrorMessage: {
    color: tokens.colorPaletteRedForeground1,
  },
});
